import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import "./index.scss"
import { minDate } from "../PeriodPicker/utils"
import useMobileView from "~/ui-rtk/hooks/mobile-view"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

import CheckboxWithLabel from "../CheckboxWithLabel"
import useDate from "~/ui-rtk/hooks/date"

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { AggregationType } from "~/ui-rtk/constants/charts"
const dayjs = useDate()

interface CustomHeaderProps {
  date: Date
  decreaseMonth: () => void
  increaseMonth: () => void
  changeYear: (year: number) => void
  prevMonthButtonDisabled: boolean
  nextMonthButtonDisabled: boolean
  showMonthYearPicker?: boolean
}

const CustomHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  changeYear,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  showMonthYearPicker,
}: CustomHeaderProps) => {
  const handleDecrease = () => {
    if (showMonthYearPicker) {
      const currentYear = dayjs(date).year()
      changeYear(currentYear - 1)
    } else {
      decreaseMonth()
    }
  }

  const handleIncrease = () => {
    if (showMonthYearPicker) {
      const currentYear = dayjs(date).year()
      changeYear(currentYear + 1)
    } else {
      increaseMonth()
    }
  }

  return (
    <div className="flex items-center justify-between px-2">
      <span className="text-md font-semibold">
        {showMonthYearPicker
          ? dayjs(date).format("YYYY")
          : dayjs(date).format("MMMM YYYY")}
      </span>
      <div className="flex gap-1">
        <button
          onClick={handleDecrease}
          disabled={prevMonthButtonDisabled}
          className="p-1 hover:bg-basic-blue/20 rounded-md disabled:opacity-50"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        <button
          onClick={handleIncrease}
          disabled={nextMonthButtonDisabled}
          className="p-1 hover:bg-basic-blue/20 rounded-md disabled:opacity-50"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

interface DateInputProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder: string
  className?: string
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
}) => (
  <div
    className={cn(
      "flex items-center bg-basic-blue/80 rounded-lg px-2 h-9",
      className,
    )}
  >
    <CalendarIcon className="w-4 h-4 mr-2" />
    <input
      type="text"
      value={value}
      onChange={onChange}
      className="bg-transparent border-none outline-none p-0 text-sm w-28"
      placeholder={placeholder}
    />
  </div>
)

// Define the props interface
interface PeriodPickerProps {
  start: Date | null
  end: Date | null
  comparisonEnabled?: boolean
  compareStart?: Date
  compareEnd?: Date
  minDate?: Date
  maxDate?: Date
  defaultPreset?: PresetType
  granularity?: AggregationType
  showMonthYearPicker?: boolean
  isMonthModeEnabled?: boolean
  isWeekModeEnabled?: boolean
  lastUpdatedDate?: Date
  showPredefinedPeriods?: boolean
  customPredefinedPeriods?: Array<{ label: string; days: number }>
  onChange: (dates: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => void
}

type PresetType =
  | "last7Days"
  | "last30Days"
  | "last3Months"
  | "last6Months"
  | "last12Months"

// Predefined period templates
const PREDEFINED_PERIODS = [
  { type: "last7Days", label: "Last 7 days", days: 7 },
  { type: "last30Days", label: "Last 30 days", days: 30 },
  { type: "last3Months", label: "Last 3 months", days: 90 },
  { type: "last6Months", label: "Last 6 months", days: 180 },
  { type: "last12Months", label: "Last 12 months", days: 365 },
]

const MONTHLY_PREDEFINED_PERIODS = [
  { type: "last3Months", label: "Last 3 months", days: 90 },
  { type: "last6Months", label: "Last 6 months", days: 180 },
  { type: "last12Months", label: "Last 12 months", days: 365 },
]

// Comparison period templates
const COMPARISON_TEMPLATES = [
  { label: "Previous period", type: "previousPeriod" },
  { label: "Same dates prior year", type: "lastYear" },
  { label: "Same days prior year", type: "sameDaysLastYear" },
]

const defaultMaxDate = new Date()
const defaultMinDate = minDate

// Format date to string
const formatDate = (date?: Date | null): string =>
  date ? dayjs(date).format("MMM D, YYYY") : ""

// Parse date from string
const parseDate = (dateStr: string): Date | null => {
  const parsed = dayjs(dateStr, "MMM D, YYYY")
  return parsed.isValid() ? parsed.toDate() : null
}

export const DateRangePicker: React.FC<PeriodPickerProps> = ({
  start,
  end,
  comparisonEnabled = true,
  compareStart,
  compareEnd,
  granularity = AggregationType.DAILY,
  minDate = defaultMinDate,
  maxDate = defaultMaxDate,
  defaultPreset = "last6Months",
  showMonthYearPicker = false,
  isMonthModeEnabled = false,
  isWeekModeEnabled = false,
  lastUpdatedDate,
  showPredefinedPeriods = true,
  customPredefinedPeriods,
  onChange,
}) => {
  // Local state
  const [isOpen, setIsOpen] = useState(false)
  const [localStart, setLocalStart] = useState<Date | null>(start)
  const [localEnd, setLocalEnd] = useState<Date | null>(end)
  const [localCompareStart, setLocalCompareStart] = useState<Date | undefined>(
    compareStart,
  )
  const [localCompareEnd, setLocalCompareEnd] = useState<Date | undefined>(
    compareEnd,
  )
  const isComparisonMode = !!compareStart && !!compareEnd
  const [localComparisonEnabled, setLocalComparisonEnabled] =
    useState(isComparisonMode)
  const [calendarSelectingStart, setCalendarSelectingStart] = useState(true)
  const [calendarCompareSelectingStart, setCalendarCompareSelectingStart] =
    useState(true)
  const [startInputValue, setStartInputValue] = useState("")
  const [endInputValue, setEndInputValue] = useState("")
  const [compareStartInputValue, setCompareStartInputValue] = useState("")
  const [compareEndInputValue, setCompareEndInputValue] = useState("")

  const popupRef = useRef<HTMLDivElement>(null)
  const isMobile = useMobileView()

  const isMonthMode = useMemo(
    () =>
      isMonthModeEnabled !== false &&
      (showMonthYearPicker || granularity === AggregationType.MONTHLY),
    [isMonthModeEnabled, granularity, showMonthYearPicker],
  )

  const isWeekMode = useMemo(
    () => isWeekModeEnabled !== false && granularity === AggregationType.WEEKLY,
    [isWeekModeEnabled, granularity],
  )

  const effectiveMaxDate = useMemo(
    () =>
      lastUpdatedDate && lastUpdatedDate < maxDate ? lastUpdatedDate : maxDate,
    [lastUpdatedDate, maxDate],
  )

  const getAdjustedDate = useCallback(
    (date: Date, isStart = false): Date => {
      let adjustedDate: Date = date
      if (isMonthMode) {
        adjustedDate = isStart
          ? dayjs(date).startOf("month").toDate()
          : dayjs(date).endOf("month").toDate()
      } else if (isWeekMode) {
        adjustedDate = isStart
          ? dayjs(date).startOf("week").toDate()
          : dayjs(date).endOf("week").toDate()
      }
      return adjustedDate
    },
    [isMonthMode, isWeekMode],
  )

  // Update input values when dates change
  useEffect(() => {
    setStartInputValue(formatDate(localStart))
    setEndInputValue(formatDate(localEnd))
    setCompareStartInputValue(formatDate(localCompareStart))
    setCompareEndInputValue(formatDate(localCompareEnd))
  }, [localStart, localEnd, localCompareStart, localCompareEnd])

  useEffect(() => {
    if (start?.getTime() !== localStart?.getTime()) {
      setLocalStart(start)
    }
  }, [start])

  useEffect(() => {
    if (end?.getTime() !== localEnd?.getTime()) {
      setLocalEnd(end)
    }
  }, [end])

  useEffect(() => {
    if (compareStart?.getTime() !== localCompareStart?.getTime()) {
      setLocalCompareStart(compareStart)
    }
  }, [compareStart])

  useEffect(() => {
    if (compareEnd?.getTime() !== localCompareEnd?.getTime()) {
      setLocalCompareEnd(compareEnd)
    }
  }, [compareEnd])

  // Apply predefined period
  const applyPredefinedPeriod = (days: number) => {
    const end = lastUpdatedDate || new Date()
    const start = dayjs(end).subtract(days, "day").toDate()
    const adjustedStart = getAdjustedDate(start, true)
    const adjustedEnd = getAdjustedDate(end, false)

    setLocalStart(adjustedStart)
    setLocalEnd(adjustedEnd)

    setCalendarSelectingStart(true)
  }

  // Apply comparison template
  const applyComparisonTemplate = (type: string) => {
    if (!localStart || !localEnd) return

    const periodLength = dayjs(localEnd).diff(dayjs(localStart), "day")

    const compareEnd = dayjs(localStart).subtract(1, "day").toDate()
    const compareStart = dayjs(compareEnd)
      .subtract(periodLength, "day")
      .toDate()

    switch (type) {
      case "previousPeriod":
        setLocalCompareStart(compareStart)
        setLocalCompareEnd(compareEnd)
        break

      case "lastYear":
        setLocalCompareStart(dayjs(localStart).subtract(1, "year").toDate())
        setLocalCompareEnd(dayjs(localEnd).subtract(1, "year").toDate())
        break

      case "sameDaysLastYear":
        setLocalCompareStart(dayjs(localStart).subtract(364, "days").toDate())
        setLocalCompareEnd(dayjs(localEnd).subtract(364, "days").toDate())
        break
    }

    setCalendarCompareSelectingStart(true)
  }

  // Handle date changes for both main and comparison calendars
  const handleDateChange = useCallback(
    (date: Date, isStart: boolean, isCompare: boolean) => {
      const adjustedDate = getAdjustedDate(date, isStart)

      if (isCompare) {
        if (isStart) {
          setLocalCompareStart(adjustedDate)
          setCalendarCompareSelectingStart(false)
        } else {
          setLocalCompareEnd(adjustedDate)
          setCalendarCompareSelectingStart(true)
        }
      } else {
        if (isStart) {
          setLocalStart(adjustedDate)
          setCalendarSelectingStart(false)
        } else {
          setLocalEnd(adjustedDate)
          setCalendarSelectingStart(true)
        }
      }
    },
    [getAdjustedDate],
  )

  // Handle text field input changes
  const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartInputValue(e.target.value)
    const parsed = parseDate(e.target.value)
    if (parsed) {
      const adjustedDate = getAdjustedDate(parsed, true)
      setLocalStart(adjustedDate)
    }
  }

  const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndInputValue(e.target.value)
    const parsed = parseDate(e.target.value)
    if (parsed) {
      const adjustedDate = getAdjustedDate(parsed, false)
      setLocalEnd(adjustedDate)
    }
  }

  const handleCompareStartInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCompareStartInputValue(e.target.value)
    const parsed = parseDate(e.target.value)
    if (parsed) {
      const adjustedDate = getAdjustedDate(parsed, true)
      setLocalCompareStart(adjustedDate)
    }
  }

  const handleCompareEndInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCompareEndInputValue(e.target.value)
    const parsed = parseDate(e.target.value)
    if (parsed) {
      const adjustedDate = getAdjustedDate(parsed, false)
      setLocalCompareEnd(adjustedDate)
    }
  }

  // Build the display text for the text field
  const getDisplayText = () => {
    const primaryRange =
      localStart && localEnd
        ? `${formatDate(localStart)} — ${formatDate(localEnd)}`
        : "Select date range"

    if (!localComparisonEnabled || !localCompareStart || !localCompareEnd) {
      return primaryRange
    }

    return `${primaryRange} vs ${formatDate(localCompareStart)} — ${formatDate(localCompareEnd)}`
  }

  return (
    <>
      {isOpen && (
        <div
          className={cn(
            "absolute z-[48] mt-2 inset-0 bg-black/40 transition-opacity duration-200",
            isOpen ? "opacity-100" : "opacity-0",
          )}
          onClick={() => {
            setIsOpen(false)
            if (localStart && localEnd) {
              onChange({
                start: localStart,
                end: localEnd,
                compareStart: localComparisonEnabled ? localCompareStart : null,
                compareEnd: localComparisonEnabled ? localCompareEnd : null,
              })
            }
          }}
        />
      )}
      <div
        className={cn("relative ui-rtk-period-picker z-[49]", {
          "month-picker-active": isMonthMode,
        })}
      >
        {/* Main TextField */}
        <div
          className={cn(
            "cursor-pointer p-system-sm z-[50] bg-system-blue-700 h-system-2xl border border-system-blue-600 text-base rounded-system-md",
            {
              "bg-system-blue-700": !isOpen,
              "bg-system-blue-600": isOpen,
            },
          )}
          onClick={() => {
            setIsOpen(!isOpen)
          }}
        >
          <div
            className={cn(
              "flex items-center justify-start gap-system-xs font-[Lato] whitespace-nowrap",
              {
                "text-system-gray-400": !isOpen,
                "text-white": isOpen,
              },
            )}
          >
            <CalendarIcon className="w-4 h-4" />
            <span>{getDisplayText()}</span>
          </div>
        </div>

        {/* Popup with animation */}
        <div
          className={cn(
            "absolute z-50 mt-system-xs right-0  transition-all duration-200 origin-top-right",
            isOpen
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-95 translate-y-2 pointer-events-none",
          )}
        >
          <div
            ref={popupRef}
            className={cn(
              "bg-basic-dark-blue/80 backdrop-blur-lg border border-basic-blue rounded-[0.625rem] px-2 py-1.5",
              {
                "flex w-[42rem]": !isMobile,
                "w-[16rem]": isMobile,
              },
            )}
          >
            {/* Left Column - Predefined Periods */}
            {showPredefinedPeriods && (
              <div
                className={cn("border-basic-blue p-system-md", {
                  "border-r pr-system-xs": !isMobile,
                  "border-b pb-system-xs": isMobile,
                })}
              >
                <div
                  className={cn("", {
                    "flex flex-col gap-system-xs": isMobile,
                    "space-y-system-xs": !isMobile,
                  })}
                >
                  <div className="text-base font-medium text-basic-neutral">
                    Presets:
                  </div>
                  {(
                    customPredefinedPeriods ??
                    (isMonthMode
                      ? MONTHLY_PREDEFINED_PERIODS
                      : PREDEFINED_PERIODS)
                  ).map(period => (
                    <button
                      key={period.label}
                      onClick={() => applyPredefinedPeriod(period.days)}
                      className={cn(
                        "btn btn-link btn-sm w-full bg-transparent border-0 p-0 text-white no-underline hover:no-underline hover:text-white/70",
                        {
                          "justify-start": !isMobile,
                          "justify-center items-center": isMobile,
                        },
                      )}
                    >
                      {period.label}
                    </button>
                  ))}
                  <hr className="my-12 h-[2px] border-t-0 bg-basic-blue" />
                  {localComparisonEnabled &&
                    COMPARISON_TEMPLATES.map(template => (
                      <button
                        key={template.label}
                        onClick={() => applyComparisonTemplate(template.type)}
                        className={cn(
                          "btn btn-link btn-sm w-full bg-transparent border-0 p-0 text-white no-underline hover:no-underline hover:text-white/70",
                          {
                            "justify-start": !isMobile,
                            "justify-center items-center": isMobile,
                          },
                        )}
                      >
                        {template.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Right Column - Calendars */}
            <div
              className={cn("border-basic-blue", {
                "p-system-md": !isMobile,
                "border-b py-system-xs": isMobile,
              })}
            >
              {/* Main Date Range Inputs */}
              <div className="flex gap-system-xs mb-system-md justify-between">
                <div className="flex items-center gap-system-xs">
                  <DateInput
                    value={startInputValue}
                    onChange={handleStartInputChange}
                    placeholder="Start Date"
                  />
                  <span className="text-basic-blue/50">-</span>
                  <DateInput
                    value={endInputValue}
                    onChange={handleEndInputChange}
                    placeholder="End Date"
                  />
                </div>
                <div className="flex items-center gap-system-xs">
                  <XMarkIcon
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => setIsOpen(false)}
                  />
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="flex flex-col gap-system-md">
                {/* Main Date Range Calendars */}
                <div>
                  <div
                    className={cn("flex gap-system-xs", {
                      "flex-row": !isMobile,
                      "flex-col": isMobile,
                    })}
                  >
                    <div>
                      <DatePicker
                        selected={localStart}
                        onChange={(date: Date) =>
                          handleDateChange(date, true, false)
                        }
                        showMonthYearPicker={isMonthMode}
                        selectsStart
                        startDate={localStart}
                        endDate={localEnd}
                        minDate={minDate}
                        maxDate={localEnd || effectiveMaxDate}
                        inline
                        disabled={!calendarSelectingStart}
                        renderCustomHeader={props => (
                          <CustomHeader
                            {...props}
                            showMonthYearPicker={isMonthMode}
                          />
                        )}
                      />
                    </div>

                    <div>
                      <DatePicker
                        selected={localEnd}
                        onChange={(date: Date) =>
                          handleDateChange(date, false, false)
                        }
                        showMonthYearPicker={isMonthMode}
                        selectsEnd
                        startDate={localStart}
                        endDate={localEnd}
                        minDate={localStart}
                        maxDate={effectiveMaxDate}
                        inline
                        disabled={calendarSelectingStart}
                        renderCustomHeader={props => (
                          <CustomHeader
                            {...props}
                            showMonthYearPicker={isMonthMode}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Comparison Toggle */}
                <div className="mb-system-md">
                  {comparisonEnabled && (
                    <div className="flex items-center gap-system-xs">
                      <CheckboxWithLabel
                        className="border-basic-pink text-basic-pink"
                        checked={localComparisonEnabled}
                        onChange={() =>
                          setLocalComparisonEnabled(!localComparisonEnabled)
                        }
                      >
                        Compare To
                      </CheckboxWithLabel>
                    </div>
                  )}
                </div>

                {/* Comparison Period */}
                {localComparisonEnabled && (
                  <div>
                    <div className="flex items-center gap-system-xs">
                      <DateInput
                        value={compareStartInputValue}
                        onChange={handleCompareStartInputChange}
                        placeholder="Start Date"
                      />
                      <span className="text-basic-blue/50">-</span>
                      <DateInput
                        value={compareEndInputValue}
                        onChange={handleCompareEndInputChange}
                        placeholder="End Date"
                      />
                    </div>
                  </div>
                )}

                {/* Comparison Date Range Calendars */}
                {localComparisonEnabled && (
                  <div>
                    <div
                      className={cn("flex gap-system-xs", {
                        "flex-row": !isMobile,
                        "flex-col": isMobile,
                      })}
                    >
                      <div>
                        <DatePicker
                          selected={localCompareStart}
                          onChange={(date: Date) =>
                            handleDateChange(date, true, true)
                          }
                          showMonthYearPicker={isMonthMode}
                          selectsStart
                          startDate={localCompareStart}
                          endDate={localCompareEnd}
                          maxDate={localCompareEnd || effectiveMaxDate}
                          disabled={!calendarCompareSelectingStart}
                          inline
                          renderCustomHeader={props => (
                            <CustomHeader
                              {...props}
                              showMonthYearPicker={isMonthMode}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <DatePicker
                          selected={localCompareEnd}
                          onChange={(date: Date) =>
                            handleDateChange(date, false, true)
                          }
                          showMonthYearPicker={isMonthMode}
                          selectsEnd
                          startDate={localCompareStart}
                          endDate={localCompareEnd}
                          minDate={localCompareStart}
                          maxDate={effectiveMaxDate}
                          disabled={calendarCompareSelectingStart}
                          inline
                          renderCustomHeader={props => (
                            <CustomHeader
                              {...props}
                              showMonthYearPicker={isMonthMode}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Apply Button */}
              <div className="mt-system-md gap-system-xs flex">
                <button
                  className="btn text-black bg-basic-white hover:bg-basic-white/60 h-[40px] min-h-[40px]"
                  onClick={() => {
                    setLocalStart(null)
                    setLocalEnd(null)
                    setLocalComparisonEnabled(false)
                    setLocalCompareStart(undefined)
                    setLocalCompareEnd(undefined)
                    applyPredefinedPeriod(
                      PREDEFINED_PERIODS.find(p => p.type === defaultPreset)
                        ?.days || 180,
                    )
                  }}
                >
                  Reset All
                </button>
                <button
                  className="btn text-black bg-basic-pink hover:bg-basic-pink/60 h-[40px] min-h-[40px]"
                  onClick={() => {
                    setIsOpen(false)
                    if (localStart && localEnd) {
                      onChange({
                        start: localStart,
                        end: localEnd,
                        compareStart: localComparisonEnabled
                          ? localCompareStart
                          : null,
                        compareEnd: localComparisonEnabled
                          ? localCompareEnd
                          : null,
                      })
                    }
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DateRangePicker
